/**
 * @module lib/election/riding-card-nav
 */
import ee from "lib/eventemitter";
import TabsPrototype from "ui/tabs";
import JumpBoxPrototype from "ui/jump-box";
import { qs, getById, toArray } from "utils/utils";
import logger from "utils/logger";

const debugTag = "[ELECTION-RIDING-CARD-NAV]";
const dynamicallyUpdateHash = false; // Causes page to jump, so it's disabled
let tabs;
let jumpBox;

/**
 * Initialize the in-page riding card navigation UI
 * @param {HTMLElement} containerEl - The root element
 */
export default function init(containerEl) {
  const container = containerEl;
  const tabsEl = qs(".c-tabs__list", container);
  const jumpBoxEl = qs(".c-jump-box", container);

  tabs = Object.create(TabsPrototype);
  jumpBox = Object.create(JumpBoxPrototype);

  if (!tabs || !tabsEl || !jumpBox || !jumpBoxEl) {
    return;
  }
  tabs.init(tabsEl, dynamicallyUpdateHash);

  jumpBox.init(jumpBoxEl, selectedOption => {
    // When a new jump box option has been selected
    logger.info(`${debugTag} jump box submit: #${selectedOption.value}`);
    revealJumpBoxTarget(selectedOption, dynamicallyUpdateHash);
    sendAnalytics(selectedOption.label);
  });

  // When a new tab has been selected
  ee.on("tabs:tab-activated", (tabsInstance, tabIndex) => {
    if (tabsInstance === tabs) {
      sendAnalytics(tabs.tabLinks[tabIndex].textContent.trim());
    }
  });

  // Toggle between jump box and tab-based navigation, depending on the breakpoint
  ee.on("windowResize.throttled", checkViewportWidth);
  checkViewportWidth();
}

/**
 * Reveal a particular jump box target element and hide the others. Normally,
 * the jump box component would jump/scroll the user down the page and focus
 * the appropriate heading, but we want it to hide and reveal elements instead.
 * @param {HTMLOptionElement} selectedOption - The currently-selected jump box option
 * @param {Boolean} updateHash - Whether to append the item's ID to the URL as a hash fragment
 */
function revealJumpBoxTarget(selectedOption, updateHash) {
  toArray(jumpBox.select.options).forEach(option => {
    const optionEl = getById(option.value);

    if (optionEl) {
      if (option === selectedOption) {
        optionEl.removeAttribute("hidden");

        if (updateHash) {
          window.location.hash = option.value;
          optionEl.focus();
        }
      } else {
        optionEl.setAttribute("hidden", "hidden");
      }
    }
  });
}

/**
 * Enable the jump box and reveal its first target element
 * (i.e. the "key ridings" panel)
 */
function enableJumpBox() {
  jumpBox.el.classList.remove("u-hidden");
  jumpBox.enable();

  tabs.tabPanels.forEach(panel => {
    panel.setAttribute("tabindex", "-1");
  });
}

/**
 * Disable the jump box
 */
function disableJumpBox() {
  jumpBox.el.classList.add("u-hidden");
  jumpBox.disable();
}

/**
 * Enable/disable the tabs and jump box, depending on the viewport width
 */
function checkViewportWidth() {
  // Since neither the tabs nor the jump box are enabled,
  // we can assume this is the initial page load
  const initialPageLoad = !tabs.enabled && !jumpBox.enabled;
  const hashExists = window.location.hash ? true : false;
  let navTypeHasChanged = false;
  let index = 0; // Display the first panel by default

  if (initialPageLoad && hashExists) {
    index = tabs.tabLinks.findIndex(a => window.location.hash.includes(a.hash));
    index = index === -1 ? 0 : index;
  } else if (tabs.currentIndex > 0) {
    index = tabs.currentIndex;
  } else if (jumpBox.select.selectedIndex > 0) {
    index = jumpBox.select.selectedIndex;
  }

  if (window.matchMedia("(min-width: 768px)").matches) {
    if (!tabs.enabled) {
      logger.info(`${debugTag} enable tabs`);
      disableJumpBox();
      tabs.enable();
      navTypeHasChanged = true;
    }
  } else {
    if (!jumpBox.enabled) {
      logger.info(`${debugTag} enable jump box`);
      tabs.disable();
      enableJumpBox();
      navTypeHasChanged = true;
    }
  }

  if (initialPageLoad) {
    activateNavPanel(index, hashExists);
  } else if (navTypeHasChanged) {
    activateNavPanel(index, false);
  }
}

/**
 * Initialize the tabs or jump box navigation
 * @param {Number} index - Index of the panel that we want to reveal
 * @param {Boolean} focusPanel - Whether to focus and scroll to the active panel after it's revealed
 */
function activateNavPanel(index, focusPanel) {
  if (!tabs.tabPanels || !tabs.tabPanels[index]) {
    return;
  }
  logger.info(`${debugTag} reveal panel: #${tabs.tabPanels[index].id}`);

  if (tabs.enabled) {
    tabs.handleTabInteraction(index, null, false, false, false);
  } else if (jumpBox.enabled) {
    jumpBox.select.selectedIndex = index;
    revealJumpBoxTarget(jumpBox.select.options.item(index), false);
  }
  // If a hash exists in the URL, focus the active panel.
  // Note: the browser should automatically jump down to the anchor unless the
  // user was previously on this page and hit the refresh button, in which case
  // Chrome will favour the user's previous scroll position. It should be
  // possible to override this behaviour via history.scrollRestoration = "manual",
  // but we aren't currenlty doing that. See: https://stackoverflow.com/questions/7035331/prevent-automatic-browser-scroll-on-refresh/31246298
  if (focusPanel) {
    tabs.tabPanels[index].focus();
  }
}

/**
 * Send data to analytics
 * @param {String} regionLabel - Name of the currently-selected region
 */
function sendAnalytics(regionLabel) {
  ee.trigger("analytics.trackGTMEvent", [{
    feature: "Elections 2021",
    type: "button",
    label: `Riding List: ${regionLabel}`,
    action: "Change Riding List"
  }, "clickEvent"]);
}
